import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import cx from "clsx";
import { useEffect, useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { ActionIcon, Box, Card, Flex, Text, Tooltip } from "@mantine/core";
import { IconMenu2 } from "@tabler/icons-react";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import { sortByReferenceIds } from "@/utils/utils";
import { updateTemplateSection } from "@/graphql/mutations";
import {
  UpdateTemplateSectionInput,
  UpdateTemplateSectionMutation,
  UpdateTemplateSectionMutationVariables,
} from "@/API";
import classes from "../TemplateContent.module.css";
import ContentFieldsTemplate from "./ContentFieldsTemplate";

const client = generateClient();
/* eslint-disable @typescript-eslint/no-explicit-any */
interface contentSubsectionTemplateProps {
  section: any;
  templateId: any;
  fetchTemplate: (templateId: string) => void;
  setSubSectionEdit: (subsection: any) => void;
  setModalEditSubSectionOpened: (opened: boolean) => void;
  setFieldEdit: (subsection: any) => void;
  setModalEditFieldOpened: (opened: boolean) => void;
}

const ContentSubsectionTemplate = ({
  section,
  templateId,
  fetchTemplate,
  setSubSectionEdit,
  setModalEditSubSectionOpened,
  setFieldEdit,
  setModalEditFieldOpened,
}: contentSubsectionTemplateProps) => {
  const [subsections, setSubsections] = useState<any>();

  const changeSectionOrder = async (newSubsectionOrder: any) => {
    const updatedSectionInput: UpdateTemplateSectionInput = {
      id: section.id,
      _version: section._version,
      subsectionOrder: newSubsectionOrder,
    };
    const updatedSectionVars: UpdateTemplateSectionMutationVariables = {
      input: updatedSectionInput,
    };
    try {
      await client.graphql<GraphQLQuery<UpdateTemplateSectionMutation>>({
        query: updateTemplateSection,
        variables: updatedSectionVars,
      });
      fetchTemplate(templateId);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (section?.subsections) {
      const subsItemsOrder = sortByReferenceIds(
        section?.subsections.items,
        section?.subsectionOrder,
      );
      section["subsections"]["items"] = subsItemsOrder;
      setSubsections(section?.subsections);
    }
  }, [section]);

  const reorderSection = ({ from, to }: any) => {
    const sectionsClone = { ...subsections };
    const item = subsections.items[from];
    subsections.items.splice(from, 1);
    subsections.items.splice(to, 0, item);
    const idsOrder = sectionsClone.items.map((item: any) => {
      return { name: item.name, id: item.id };
    });
    setSubsections(sectionsClone);
    changeSectionOrder(idsOrder.map((order: any) => order.id));
  };

  const items = subsections?.items?.map((subSecItem: any, index: number) => (
    <Draggable
      key={`draggable-sub-section-${subSecItem.id}`}
      index={index}
      draggableId={subSecItem.id}
    >
      {(provided, snapshot) => (
        <Box
          className={cx("header-subsection", {
            [classes.itemDragging]: snapshot.isDragging,
          })}
          key={subSecItem.id}
          p={8}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <Card bg={"rgb(198, 198, 200)"} radius={0} p={5}>
            <Flex align={"center"} justify={"space-between"}>
              <Text fz={12} fw={500} tt={"uppercase"} p={10}>
                {subSecItem.name}
              </Text>
              <Flex>
                <Tooltip label="Edit subsection" withArrow>
                  <ActionIcon
                    bg={"transparent"}
                    onClick={() => {
                      setSubSectionEdit(subSecItem);
                      setModalEditSubSectionOpened(true);
                    }}
                  >
                    <FaPencilAlt size={14} color="black" />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Move subsection" withArrow>
                  <ActionIcon
                    {...provided.dragHandleProps}
                    bg={"transparent"}
                    className="btn-reorder"
                  >
                    <IconMenu2 size={18} color="black" />
                  </ActionIcon>
                </Tooltip>
              </Flex>
            </Flex>
          </Card>
          {subSecItem?.fields?.items && (
            <ContentFieldsTemplate
              sectionId={section.id}
              subsection={subSecItem}
              fetchTemplate={fetchTemplate}
              setFieldEdit={setFieldEdit}
              setModalEditFieldOpened={setModalEditFieldOpened}
              templateId={templateId}
            />
          )}
        </Box>
      )}
    </Draggable>
  ));

  return (
    <DragDropContext
      onDragEnd={({ destination, source }) => {
        reorderSection({
          from: source.index,
          to: destination?.index || 0,
        });
      }}
    >
      <Droppable droppableId="dnd-list-subsection" direction="vertical">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default ContentSubsectionTemplate;
